import React, { useState, useEffect } from 'react';
import { Menu, Button, Layout, Icon, Tag, Avatar } from 'antd';
import VoiceLogsPage from './VoiceLogsPage';
import RefuelReconciliation from './RefuelReconciliation';
import IoTDailyReportAnalysis from './IoTDailyReportAnalysis';
import FuelAlertAnalysis from './FuelAlertAnalysis';
import ScheduleMaintenanceTracker from './ScheduleMaintenanceTracker';
import VehicleTrackingShare from './VehicleTrackingShare';
import VehicleTrackingView from './VehicleTrackingView';
import AssetClassificationManager from './AssetClassificationManager';
import IoTSummaryReport from './IoTSummaryReport';
import ConsumptionRateVerifier from './ConsumptionRateVerifier';
import StableFuelConsumptionAnalysis from './StableFuelConsumptionAnalysis';
import ApiTester from './ApiTester';
import ConsumptionRateAnalyzer from './ConsumptionRateAnalyzer';
import HDIConsumptionComparison from './HDIConsumptionComparison';
import AlertInbox from './AlertInbox';
import ClosedAlertsReport from './ClosedAlertsReport';
import FullTankMileageReport from './FullTankMileageReport';
import MonthlyFullTankReport from './MonthlyFullTankReport';
import BowserComparisonReport from './BowserComparisonReport';
import AssetImportMapper from './AssetImportMapper';
import SmartAssetImport from './SmartAssetImport';
import AssetUtilizationStudio from './AssetUtilizationStudio';
import SparesStockTaking from './SparesStockTaking';
import SparesReconciliation from './SparesReconciliation';
import AssetReclassification from './AssetReclassification';
import OverspeedGreenDrivingReport from './OverspeedGreenDrivingReport';
import AssetLagProfileReport from './AssetLagProfileReport';
import SpaceUtilizationReport from './SpaceUtilizationReport';
import AuditCoverageReport from './AuditCoverageReport';
import PassiveAssetVisibility from './PassiveAssetVisibility';
import DeviceComparisonReport from './DeviceComparisonReport';
import BleAssetTracker from './BleAssetTracker';
import BleReaderTracker from './BleReaderTracker';
import SalesAssetReport from './SalesAssetReport';
import FuelPurchaseReport from './FuelPurchaseReport';
import FuelStockIssueReport from './FuelStockIssueReport';
import DGOperatingLevelReport from './DGOperatingLevelReport';
import LoginPage from './LoginPage';
import { isAuthenticated, logout, getUserData } from './utils/authService';
import { getServerType, getServerDisplayName } from './utils/serverConfig';
import 'antd/dist/antd.css';
import './App.css';

const { Header, Sider, Content, Footer } = Layout;
const { SubMenu } = Menu;

const READY_TO_USE_ITEMS = [
  { key: 'fuel-alert-analysis', icon: 'alert', label: 'Fuel Alert Analysis', subtitle: 'Review anomalies, validate alerts, and close fuel events quickly' },
  { key: 'iot-summary-report', icon: 'bar-chart', label: 'Sensor Fuel and Utilization Report', subtitle: 'Track fleet fuel behavior and utilization trends from IoT signals' },
  { key: 'stable-fuel-analysis', icon: 'line-chart', label: 'Stable Fuel Consumption Analysis', subtitle: 'Analyze stable periods to derive precise fuel consumption patterns' },
  { key: 'hdi-consumption-comparison', icon: 'swap', label: 'HDI Consumption Comparison', subtitle: 'Compare consumption distributions and benchmark model performance' },
  { key: 'full-tank-mileage-report', icon: 'dashboard', label: 'Full Tank Consumption Report', subtitle: 'Audit full-tank cycles with consumption, rate, and variance views' },
  { key: 'monthly-full-tank-report', icon: 'bar-chart', label: 'Monthly Full Tank Report', subtitle: 'Summarize full-tank performance and monthly consumption outcomes' },
  { key: 'bowser-comparison', icon: 'reconciliation', label: 'Bowser Dispense vs Issue Comparison', subtitle: 'Reconcile bowser sensor dispense against issue entries' },
  { key: 'closed-alerts-report', icon: 'file-done', label: 'Alerts Status Report', subtitle: 'Track open and closed alerts by workflow status, with decisions and actions taken' },
  { key: 'ble-asset-tracker', icon: 'environment', label: 'BLE Asset Tracker', subtitle: 'Track one BLE tag: where it dwelled, who detected it, and how it moved between locations' },
  { key: 'ble-reader-tracker', icon: 'car', label: 'BLE Reader Tracker', subtitle: 'Track one reader: its route over the period with the BLE assets it detected and towed' },
  { key: 'sales-asset-report', icon: 'shopping-cart', label: 'Sales Asset Report', subtitle: 'List all assets sold within a date range across sale vouchers' },
  { key: 'fuel-purchase-report', icon: 'shopping-cart', label: 'Fuel Purchase Report', subtitle: 'Total fuel purchased by period or filter, market vs bowser-issued' },
  { key: 'fuel-stock-issue-report', icon: 'apartment', label: 'Fuel Stock & Issue Report', subtitle: 'Tank/bowser opening, received, issued, closing with Sankey flow and reconciliation' },
  { key: 'dg-operating-level', icon: 'thunderbolt', label: 'DG Operating Level & Capacity Utilisation', subtitle: 'How hard each KWH-metered DG actually runs, and whether its kVA rating fits the measured load' }
];

const UNDER_LAB_ITEMS = [
  { key: 'voice-logs', icon: 'sound', label: 'Voice Logs', subtitle: 'Search, review, and audit voice interactions and extracted outcomes' },
  { key: 'refuel-reconciliation', icon: 'reload', label: 'Refuel Reconciliation', subtitle: 'Reconcile manual refuels with IoT events and matching confidence' },
  { key: 'iot-daily-report', icon: 'file-text', label: 'IoT Daily Report Analysis', subtitle: 'Analyze daily IoT reports with automated quality and exception checks' },
  { key: 'schedule-maintenance', icon: 'calendar', label: 'Schedule Maintenance', subtitle: 'Plan and monitor preventive maintenance schedules and due activities' },
  { key: 'share-tracking', icon: 'share-alt', label: 'Share Vehicle Tracking', subtitle: 'Generate secure tracking links and share live vehicle movement views' },
  { key: 'asset-classification', icon: 'tags', label: 'Asset Classification', subtitle: 'Manage asset taxonomy and classification consistency across the fleet' },
  { key: 'consumption-rate-verifier', icon: 'check-circle-o', label: 'Consumption Rate Verifier', subtitle: 'Validate computed rates against expected fuel and runtime intervals' },
  { key: 'api-tester', icon: 'api', label: 'API Tester', subtitle: 'Test endpoints, payloads, and responses with quick debugging workflows' },
  { key: 'consumption-rate-analyzer', icon: 'calculator', label: 'Consumption Rate Analyzer', subtitle: 'Run deeper statistical analysis for model and segment rate behavior' },
  { key: 'alert-inbox', icon: 'inbox', label: 'Alert Inbox', subtitle: 'Triage incoming alerts, assign actions, and track investigation progress' },
  { key: 'asset-import-mapper', icon: 'upload', label: 'Asset Import Mapper', subtitle: 'Map import files to schema fields for clean asset onboarding' },
  { key: 'smart-asset-import', icon: 'thunderbolt', label: 'Smart Asset Import', subtitle: 'Auto-detect, map, and validate messy asset files into AssetPro import format' },
  { key: 'asset-utilization', icon: 'environment', label: 'Asset Utilization', subtitle: 'Measure asset activity, movement context, and operational utilization' },
  { key: 'spares-stock-taking', icon: 'profile', label: 'Spares Stock Taking', subtitle: 'Create stock-taking vouchers and capture physically counted spares' },
  { key: 'spares-reconciliation', icon: 'solution', label: 'Spares Reconciliation', subtitle: 'Compare counted spares against inventory and resolve variances quickly' },
  { key: 'asset-reclassification', icon: 'interaction', label: 'Asset Reclassification', subtitle: 'Reclassify assets using AI with taxonomy-first approach' },
  { key: 'overspeed-green-driving', icon: 'dashboard', label: 'Overspeeding & Green Driving Events', subtitle: 'Compare existing vs. new overspeed detection and green driving events' },
  { key: 'asset-lag-profile', icon: 'clock-circle-o', label: 'Asset Lag Profile Report', subtitle: 'Measure telemetry ingestion lag for a selected asset and time range' },
  { key: 'space-utilization', icon: 'area-chart', label: 'Space Utilization Report', subtitle: 'Analyze physical space occupied by assets across locations and models' },
  { key: 'audit-coverage-report', icon: 'safety-certificate', label: 'Audit Coverage Report', subtitle: 'Track QR tagging, AST verifications, and PV discoveries for an audit voucher' },
  { key: 'passive-asset-visibility', icon: 'wifi', label: 'Passive Asset Visibility', subtitle: 'Last-seen view of Bluetooth beacon and RFID assets detected by nearby readers' },
  { key: 'device-comparison', icon: 'deployment-unit', label: 'AssetPro ↔ IoT-Ops Device Comparison', subtitle: 'Compare AssetPro asset/device links against the IoT-Ops registry and surface mismatches' }
];

const ALL_ITEMS = [...READY_TO_USE_ITEMS, ...UNDER_LAB_ITEMS];
const VALID_KEYS = new Set(ALL_ITEMS.map((item) => item.key));
const TITLE_ALIAS_MATCH = {
  'fuel alert analysis': ['fuel consumption alert', 'ai fuel alert analysis']
};

const normalizeText = (value) =>
  String(value || '')
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const isLikelyDuplicateTitle = (candidateText, menuTitle) => {
  const candidate = normalizeText(candidateText);
  const title = normalizeText(menuTitle);
  if (!candidate || !title) return false;

  const aliases = TITLE_ALIAS_MATCH[title] || [];
  if (aliases.some((alias) => candidate.includes(alias))) {
    return true;
  }

  if (candidate === title || candidate.startsWith(title) || title.startsWith(candidate)) {
    return true;
  }

  const titleWords = title.split(' ').filter((word) => word.length > 2);
  if (titleWords.length === 0) return false;
  const overlap = titleWords.filter((word) => candidate.includes(word)).length;
  return overlap / titleWords.length >= 0.75;
};

function App() {
  const getMenuFromHash = () => {
    const hash = window.location.hash;
    if (hash && hash.startsWith('#/')) {
      let menuKey = hash.replace('#/', '').split('/')[0];
      if (menuKey.includes('?')) {
        menuKey = menuKey.split('?')[0];
      }
      if (VALID_KEYS.has(menuKey)) {
        return menuKey;
      }
    }
    return 'fuel-alert-analysis';
  };

  const [selectedMenu, setSelectedMenu] = useState(getMenuFromHash());
  const [collapsed, setCollapsed] = useState(false);
  const [showTrackingView, setShowTrackingView] = useState(false);
  const [shareId, setShareId] = useState(null);
  const [authenticated, setAuthenticated] = useState(false);
  const [userData, setUserData] = useState(null);
  const [checkingAuth, setCheckingAuth] = useState(true);
  const [appVersion, setAppVersion] = useState('1.0.0');
  const [buildDate, setBuildDate] = useState(null);

  useEffect(() => {
    const checkAuth = () => {
      const isAuth = isAuthenticated();
      setAuthenticated(isAuth);
      if (isAuth) {
        setUserData(getUserData());
      }
      setCheckingAuth(false);
    };

    checkAuth();

    const loadVersion = async () => {
      try {
        const response = await fetch('/version.json?' + Date.now());
        if (response.ok) {
          const data = await response.json();
          setAppVersion(data.version || '1.0.0');
          if (data.buildDate) {
            const date = new Date(data.buildDate);
            setBuildDate(
              date.toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
              })
            );
          }
        }
      } catch (error) {
        console.warn('Could not load version:', error);
      }
    };

    loadVersion();
  }, []);

  useEffect(() => {
    if (!authenticated) return;

    const path = window.location.pathname;
    const hash = window.location.hash;

    if (path.includes('/track/')) {
      const id = path.split('/track/')[1]?.split('/')[0] || hash.replace('#track/', '');
      if (id) {
        setShareId(id);
        setShowTrackingView(true);
        return;
      }
    }

    if (hash.startsWith('#track/')) {
      const id = hash.replace('#track/', '');
      if (id) {
        setShareId(id);
        setShowTrackingView(true);
      }
    }
  }, [authenticated]);

  useEffect(() => {
    const handleHashChange = () => setSelectedMenu(getMenuFromHash());
    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  const handleMenuSelect = (key) => {
    setSelectedMenu(key);
    window.location.hash = `#/${key}`;
  };

  const handleLoginSuccess = (loggedInUserData) => {
    // Clear reclassification cache from any previous session/org
    localStorage.removeItem('reclass_results');
    setUserData(loggedInUserData);
    setAuthenticated(true);
  };

  const handleLogout = () => {
    logout();
    setAuthenticated(false);
    setUserData(null);
  };

  const selectedItem = ALL_ITEMS.find((item) => item.key === selectedMenu);
  const selectedTitle = selectedItem ? selectedItem.label : 'Fuel Alert Analysis';
  const selectedSubtitle = selectedItem ? selectedItem.subtitle : 'Review anomalies, validate alerts, and close fuel events quickly';
  const isProduction = getServerType() === 'production';

  useEffect(() => {
    if (checkingAuth || !authenticated || showTrackingView) return undefined;

    const root = document.querySelector('.aim-page-content');
    if (!root) return undefined;

    const dedupe = () => {
      root.querySelectorAll('.aim-duplicate-page-title').forEach((el) => {
        el.classList.remove('aim-duplicate-page-title');
      });

      const containerRect = root.getBoundingClientRect();
      const candidates = root.querySelectorAll(
        'h1, h2, h3, h4, .ant-typography, .ant-card-head-title, .ant-page-header-heading-title'
      );

      for (const el of candidates) {
        const rect = el.getBoundingClientRect();
        const topOffset = rect.top - containerRect.top;
        if (topOffset < -8 || topOffset > 220) continue;

        const text = el.textContent || '';
        if (text.trim().length < 8) continue;

        if (isLikelyDuplicateTitle(text, selectedTitle)) {
          el.classList.add('aim-duplicate-page-title');
          break;
        }
      }
    };

    dedupe();
    const timer = setTimeout(dedupe, 150);
    return () => clearTimeout(timer);
  }, [selectedMenu, selectedTitle, checkingAuth, authenticated, showTrackingView]);

  if (checkingAuth) {
    return (
      <div className="aim-loading-screen">
        <div className="aim-loading-card">
          <Icon type="sync" spin />
          <span>Loading AIM Dashboard...</span>
        </div>
      </div>
    );
  }

  if (!authenticated) {
    return <LoginPage onLoginSuccess={handleLoginSuccess} />;
  }

  if (showTrackingView) {
    return <VehicleTrackingView shareId={shareId} />;
  }

  const renderContent = () => {
    switch (selectedMenu) {
      case 'voice-logs':
        return <VoiceLogsPage />;
      case 'refuel-reconciliation':
        return <RefuelReconciliation />;
      case 'iot-daily-report':
        return <IoTDailyReportAnalysis />;
      case 'fuel-alert-analysis':
        return <FuelAlertAnalysis />;
      case 'schedule-maintenance':
        return <ScheduleMaintenanceTracker />;
      case 'share-tracking':
        return <VehicleTrackingShare />;
      case 'asset-classification':
        return <AssetClassificationManager />;
      case 'iot-summary-report':
        return <IoTSummaryReport />;
      case 'consumption-rate-verifier':
        return <ConsumptionRateVerifier />;
      case 'stable-fuel-analysis':
        return <StableFuelConsumptionAnalysis />;
      case 'api-tester':
        return <ApiTester />;
      case 'consumption-rate-analyzer':
        return <ConsumptionRateAnalyzer />;
      case 'hdi-consumption-comparison':
        return <HDIConsumptionComparison />;
      case 'alert-inbox':
        return <AlertInbox />;
      case 'closed-alerts-report':
        return <ClosedAlertsReport />;
      case 'full-tank-mileage-report':
        return <FullTankMileageReport />;
      case 'monthly-full-tank-report':
        return <MonthlyFullTankReport />;
      case 'bowser-comparison':
        return <BowserComparisonReport />;
      case 'asset-import-mapper':
        return <AssetImportMapper />;
      case 'smart-asset-import':
        return <SmartAssetImport />;
      case 'asset-utilization':
        return <AssetUtilizationStudio />;
      case 'spares-stock-taking':
        return <SparesStockTaking />;
      case 'spares-reconciliation':
        return <SparesReconciliation />;
      case 'asset-reclassification':
        return <AssetReclassification />;
      case 'overspeed-green-driving':
        return <OverspeedGreenDrivingReport />;
      case 'asset-lag-profile':
        return <AssetLagProfileReport />;
      case 'space-utilization':
        return <SpaceUtilizationReport />;
      case 'audit-coverage-report':
        return <AuditCoverageReport />;
      case 'passive-asset-visibility':
        return <PassiveAssetVisibility />;
      case 'device-comparison':
        return <DeviceComparisonReport />;
      case 'ble-asset-tracker':
        return <BleAssetTracker />;
      case 'ble-reader-tracker':
        return <BleReaderTracker />;
      case 'sales-asset-report':
        return <SalesAssetReport />;
      case 'fuel-purchase-report':
        return <FuelPurchaseReport />;
      case 'fuel-stock-issue-report':
        return <FuelStockIssueReport />;
      case 'dg-operating-level':
        return <DGOperatingLevelReport />;
      default:
        return <FuelAlertAnalysis />;
    }
  };

  return (
    <Layout className="aim-app-shell">
      <Header className="aim-topbar">
        <div className="aim-brand-wrap">
          <button
            type="button"
            className="aim-collapse-btn"
            onClick={() => setCollapsed((prev) => !prev)}
            aria-label={collapsed ? 'Expand navigation menu' : 'Collapse navigation menu'}
          >
            <Icon type={collapsed ? 'menu-unfold' : 'menu-fold'} />
          </button>
          <div className="aim-brand">
            <h1>AIM Dashboard</h1>
            <p>Asset Intelligence and Monitoring</p>
          </div>
          <Tag className="aim-server-tag" color={isProduction ? 'red' : 'blue'}>
            {getServerDisplayName()}
          </Tag>
        </div>

        <div className="aim-user-wrap">
          <div className="aim-user-meta" aria-label="Signed in user">
            <Avatar icon="user" className="aim-user-avatar" />
            <div>
              {userData?.organisation && (
                <div className="aim-org-name">
                  {typeof userData.organisation === 'string'
                    ? userData.organisation
                    : userData.organisation.name || ''}
                </div>
              )}
              {userData?.email && <div className="aim-user-email">{userData.email}</div>}
            </div>
          </div>
          <Button type="default" icon="logout" onClick={handleLogout} className="aim-logout-btn">
            Logout
          </Button>
        </div>
      </Header>

      <Layout className="aim-main-layout">
        <Sider
          collapsible
          trigger={null}
          collapsed={collapsed}
          width={280}
          className="aim-sidebar"
        >
          <div className="aim-sidebar-title">Navigation</div>
          <Menu
            mode="inline"
            selectedKeys={[selectedMenu]}
            onSelect={({ key }) => handleMenuSelect(key)}
            defaultOpenKeys={['ready-to-use', 'under-lab']}
            inlineCollapsed={collapsed}
            className="aim-nav"
          >
            <SubMenu
              key="ready-to-use"
              title={
                <span>
                  <Icon type="check-circle" />
                  <span>Ready to Use</span>
                </span>
              }
            >
              {READY_TO_USE_ITEMS.map((item) => (
                <Menu.Item key={item.key}>
                  <Icon type={item.icon} />
                  <span>{item.label}</span>
                </Menu.Item>
              ))}
            </SubMenu>

            <SubMenu
              key="under-lab"
              title={
                <span>
                  <Icon type="experiment" />
                  <span>Under Lab</span>
                </span>
              }
            >
              {UNDER_LAB_ITEMS.map((item) => (
                <Menu.Item key={item.key}>
                  <Icon type={item.icon} />
                  <span>{item.label}</span>
                </Menu.Item>
              ))}
            </SubMenu>
          </Menu>
        </Sider>

        <Layout className={`aim-content-layout ${collapsed ? 'is-collapsed' : ''}`}>
          <Content className="aim-content-area">
            <div className="aim-page-header">
              <h2>{selectedTitle}</h2>
              <p>{selectedSubtitle}</p>
            </div>
            <div className="aim-page-content">{renderContent()}</div>
          </Content>

          <Footer className="aim-footer">
            AIM Dashboard v{appVersion}
            {buildDate && ` • Built on ${buildDate}`}
          </Footer>
        </Layout>
      </Layout>
    </Layout>
  );
}

export default App;
